import type { AnalysisRun, InitialStatementValidation } from "@/types/analysisRun";

import CollapsibleSection from "./CollapsibleSection";

function fmtMoney(value: number | null | undefined): string {
  if (value == null) return "—";
  return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function fmtSigned(value: number | null | undefined): string {
  if (value == null) return "—";
  if (value > 0) return `+${fmtMoney(value)}`;
  return fmtMoney(value);
}

/** Path 5 stores the check on the Plaid run's rollups, after the full feed is labeled. */
export function initialStatementValidation(
  run: Pick<AnalysisRun, "analysis_json"> | null | undefined
): InitialStatementValidation | null {
  const rollups = run?.analysis_json?.rollups;
  if (!rollups || typeof rollups !== "object") return null;
  const value = rollups.initial_validation;
  if (!value || typeof value !== "object" || !value.status) return null;
  return value;
}

function accountLabel(bank: string, account: string): string {
  return [bank, account].filter(Boolean).join(" ") || "Unknown account";
}

function summaryText(validation: InitialStatementValidation): string {
  const { summary } = validation;
  if (validation.status === "no_initial_statements") return "No statement analysis";
  if (validation.status === "no_initial_periods") return "No comparable periods";
  if (summary.matched_count === 0) return "No matching Plaid accounts";
  const plaid = fmtMoney(summary.plaid_true_revenue);
  const initial = fmtMoney(summary.initial_true_revenue);
  return `Plaid ${plaid} vs statements ${initial}`;
}

function comparedLead(validation: InitialStatementValidation): string {
  const { summary } = validation;
  if (summary.matched_count === 0) {
    const count = summary.window_count;
    return `None of the ${count} uploaded statement periods could be matched to a Plaid account.`;
  }
  const plaid = fmtMoney(summary.plaid_true_revenue);
  const initial = fmtMoney(summary.initial_true_revenue);
  const delta = fmtSigned(summary.true_revenue_delta);
  return (
    `On ${summary.matched_count} of ${summary.window_count} uploaded statement periods, ` +
    `Plaid true revenue is ${plaid} versus ${initial} from the statements (${delta}).`
  );
}

interface Props {
  validation: InitialStatementValidation;
}

/**
 * Shown under AR Analysis once a Path 5 Plaid run has compared the initial
 * statement accounts and periods with the labeled Plaid feed.
 */
const InitialStatementCheck: React.FC<Props> = ({ validation }) => {
  const { summary } = validation;

  return (
    <CollapsibleSection
      title="Initial statement check"
      summary={<span className="text-sm text-muted-foreground">{summaryText(validation)}</span>}
      defaultOpen
    >
      {validation.status === "no_initial_statements" && (
        <p className="text-sm text-muted-foreground">
          No completed statement analysis to compare. The Plaid feed was still analyzed in full.
        </p>
      )}
      {validation.status === "no_initial_periods" && (
        <p className="text-sm text-muted-foreground">
          The statement analysis has no usable account periods to compare.
        </p>
      )}
      {validation.status === "compared" && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {comparedLead(validation)}
            {summary.account_missing_count > 0 &&
              ` ${summary.account_missing_count} account${
                summary.account_missing_count === 1 ? "" : "s"
              } not in the Plaid report.`}
            {summary.unmatchable_count > 0 &&
              ` ${summary.unmatchable_count} statement${
                summary.unmatchable_count === 1 ? "" : "s"
              } had no usable account number.`}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="py-2 pr-4">Account</th>
                  <th className="py-2 pr-4">Period</th>
                  <th className="py-2 pr-4 text-right">Initial revenue</th>
                  <th className="py-2 pr-4 text-right">Plaid revenue</th>
                  <th className="py-2 pr-4 text-right">Difference</th>
                  <th className="py-2">Note</th>
                </tr>
              </thead>
              <tbody>
                {validation.windows.map((window) => (
                  <tr
                    key={`${window.statement_id}-${window.period_start}`}
                    className="border-b align-top last:border-0"
                  >
                    <td className="py-2 pr-4">
                      <div className="font-medium">
                        {accountLabel(window.bank_name, window.account_number)}
                      </div>
                      {window.filename && (
                        <div className="text-xs text-muted-foreground">{window.filename}</div>
                      )}
                    </td>
                    <td className="whitespace-nowrap py-2 pr-4">
                      {window.period_start} – {window.period_end}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {fmtMoney(window.initial.true_revenue)}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {fmtMoney(window.plaid?.true_revenue)}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {fmtSigned(window.delta?.true_revenue)}
                    </td>
                    <td className="py-2 text-muted-foreground">{window.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
};

export default InitialStatementCheck;
